import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { ImagePlus, MapPin, Euro, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { processImages } from "@/lib/imageUtils";

const steps = ["Votre bois", "Détails", "Photos & prix"];

const CreateListing = () => {
  const { user, loading, roles } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [images, setImages] = useState<File[]>([]);
  const [sellerCommitment, setSellerCommitment] = useState(false);
  const [phoneCommitment, setPhoneCommitment] = useState(false);
  const [form, setForm] = useState({
    species: "",
    seasoning: "",
    length: "",
    split: "",
    title: "",
    description: "",
    volume: "",
    stock: "",
    min_steres: "",
    price: "",
    location: "",
    phone: "",
  });

  const update = (key: string, value: string) => setForm((f) => ({ ...f, [key]: value }));

  if (loading) return null;
  if (!user) return <Navigate to="/auth" replace />;

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).slice(0, 5);
    try {
      const processed = await processImages(files);
      setImages(processed);
      toast({ title: "Photos optimisées", description: `${processed.length} image(s) redimensionnée(s) et convertie(s) en WebP.` });
    } catch {
      setImages(files); // fallback to originals
    }
  };

  const handlePublish = async () => {
    if (!sellerCommitment || !phoneCommitment) {
      toast({ title: "Engagement requis", description: "Veuillez cocher toutes les cases d'engagement avant de publier.", variant: "destructive" });
      return;
    }
    if (!form.phone.trim()) {
      toast({ title: "Téléphone requis", description: "Veuillez renseigner votre numéro de téléphone.", variant: "destructive" });
      return;
    }
    setSubmitting(true);

    try {
      if (!roles.includes("seller")) {
        await supabase.from("user_roles").insert({ user_id: user.id, role: "seller" as any });
      }

      const imageUrls: string[] = [];
      for (const file of images) {
        const path = `${user.id}/${Date.now()}-${file.name}`;
        const { error: uploadError } = await supabase.storage
          .from("listing-images")
          .upload(path, file);
        if (!uploadError) {
          const { data: urlData } = supabase.storage
            .from("listing-images")
            .getPublicUrl(path);
          imageUrls.push(urlData.publicUrl);
        }
      }

      const volume = parseFloat(form.volume) || 1;
      const stock = parseFloat(form.stock) || volume;

      // Update seller phone in profile
      await supabase
        .from("profiles")
        .update({ phone: form.phone })
        .eq("user_id", user.id);

      const { error } = await supabase.from("listings").insert({
        seller_id: user.id,
        title: form.title,
        description: form.description,
        species: form.species,
        seasoning: form.seasoning,
        length: form.length,
        split: form.split === "oui",
        volume,
        stock,
        min_steres: parseFloat(form.min_steres) || 1,
        price_per_stere: parseFloat(form.price) || 0,
        location: form.location,
        image_urls: imageUrls,
      } as any);

      if (error) throw error;

      const isAdminUser = roles.includes("admin");
      toast({
        title: isAdminUser ? "Annonce publiée !" : "Annonce soumise !",
        description: isAdminUser ? "Votre annonce est maintenant visible." : "Votre annonce est en attente de validation par l'équipe ChimayBois.",
      });
      navigate("/tableau-de-bord");
    } catch (err: any) {
      toast({ title: "Erreur", description: err.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <div className="container py-8 flex-1 max-w-2xl">
        <h1 className="font-display text-3xl font-bold mb-2">Publier une annonce</h1>
        <p className="text-muted-foreground mb-8">C'est gratuit. Vos coordonnées restent cachées jusqu'à la réservation.</p>

        {/* Steps indicator */}
        <div className="flex items-center gap-2 mb-8">
          {steps.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                i <= step ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
              }`}>
                {i < step ? <Check className="h-4 w-4" /> : i + 1}
              </div>
              <span className={`text-sm hidden sm:inline ${i <= step ? "text-foreground font-medium" : "text-muted-foreground"}`}>{s}</span>
              {i < steps.length - 1 && <div className="w-8 h-px bg-border" />}
            </div>
          ))}
        </div>

        {/* Step 1 */}
        {step === 0 && (
          <div className="space-y-5 animate-fade-in">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Essence de bois</label>
              <Select value={form.species} onValueChange={(v) => update("species", v)}>
                <SelectTrigger><SelectValue placeholder="Sélectionnez..." /></SelectTrigger>
                <SelectContent>
                  {["Chêne", "Hêtre", "Frêne", "Mélange", "Autre"].map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Séchage</label>
              <Select value={form.seasoning} onValueChange={(v) => update("seasoning", v)}>
                <SelectTrigger><SelectValue placeholder="Sélectionnez..." /></SelectTrigger>
                <SelectContent>
                  {["Sec (>2 ans)", "Demi-sec (1 an)", "Vert (frais)"].map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Longueur des bûches</label>
              <Select value={form.length} onValueChange={(v) => update("length", v)}>
                <SelectTrigger><SelectValue placeholder="Sélectionnez..." /></SelectTrigger>
                <SelectContent>
                  {["25cm", "33cm", "50cm", "1m"].map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Fendu ?</label>
              <Select value={form.split} onValueChange={(v) => update("split", v)}>
                <SelectTrigger><SelectValue placeholder="Sélectionnez..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="oui">Oui, fendu</SelectItem>
                  <SelectItem value="non">Non, en rondins</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Step 2 */}
        {step === 1 && (
          <div className="space-y-5 animate-fade-in">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Titre de l'annonce</label>
              <Input value={form.title} onChange={(e) => update("title", e.target.value)} placeholder="Ex: Chêne sec 2 ans — bûches 33cm" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Description</label>
              <Textarea value={form.description} onChange={(e) => update("description", e.target.value)} placeholder="Décrivez votre bois, les conditions de stockage, la livraison possible..." rows={4} />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block flex items-center gap-2"><MapPin className="h-4 w-4" /> Localisation</label>
              <Input value={form.location} onChange={(e) => update("location", e.target.value)} placeholder="Ex: Chimay, Couvin, Momignies..." />
            </div>
          </div>
        )}

        {/* Step 3 */}
        {step === 2 && (
          <div className="space-y-5 animate-fade-in">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1.5 flex items-center gap-1"><Euro className="h-4 w-4" /> Prix par stère (€)</label>
                <Input type="number" value={form.price} onChange={(e) => update("price", e.target.value)} placeholder="85" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Volume minimum par commande (stères)</label>
                <Input type="number" value={form.volume} onChange={(e) => update("volume", e.target.value)} placeholder="5" />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-1.5 block">Stock total disponible (stères)</label>
              <Input type="number" value={form.stock} onChange={(e) => update("stock", e.target.value)} placeholder="Ex: 20" />
              <p className="text-xs text-muted-foreground mt-1">Le stock diminuera automatiquement à chaque réservation confirmée.</p>
            </div>

            <div>
              <label className="text-sm font-medium mb-1.5 block">Nombre de stères minimum pour livraison *</label>
              <Input type="number" value={form.min_steres} onChange={(e) => update("min_steres", e.target.value)} placeholder="Ex: 3" />
              <p className="text-xs text-muted-foreground mt-1">L'acheteur verra cette info pour savoir s'il peut être livré.</p>
            </div>

            <div>
              <label className="text-sm font-medium mb-1.5 block">Photos</label>
              <label className="border-2 border-dashed rounded-lg p-8 text-center text-muted-foreground hover:border-primary/40 transition-colors cursor-pointer block">
                <input type="file" accept="image/*" multiple onChange={handleImageChange} className="hidden" />
                <ImagePlus className="h-8 w-8 mx-auto mb-2" />
                {images.length > 0 ? (
                  <p className="text-sm text-primary font-medium">{images.length} photo{images.length > 1 ? "s" : ""} sélectionnée{images.length > 1 ? "s" : ""}</p>
                ) : (
                  <>
                    <p className="text-sm">Cliquez pour ajouter des photos</p>
                    <p className="text-xs mt-1">JPG, PNG — max 5 photos</p>
                  </>
                )}
              </label>
              {images.length > 0 && (
                <div className="flex gap-2 mt-3">
                  {images.map((img, i) => (
                    <img key={i} src={URL.createObjectURL(img)} alt="" className="w-16 h-16 rounded-md object-cover" />
                  ))}
                </div>
              )}
            </div>

            {/* Phone field */}
            <div>
              <label className="text-sm font-medium mb-1.5 block">📞 Numéro de téléphone (obligatoire)</label>
              <Input
                type="tel"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="Ex: +32 470 12 34 56"
              />
              <p className="text-xs text-muted-foreground mt-1">Ce numéro sera transmis à l'acheteur uniquement après le paiement de l'acompte.</p>
            </div>

            {/* Seller commitment */}
            <div className="bg-muted rounded-lg p-4 border space-y-3">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="commitment"
                  checked={sellerCommitment}
                  onCheckedChange={(checked) => setSellerCommitment(checked === true)}
                  className="mt-0.5"
                />
                <label htmlFor="commitment" className="text-sm text-foreground leading-relaxed cursor-pointer">
                  Je m'engage à <strong>déduire les 10 € d'acompte</strong> du prix final lors de la livraison et à <strong>maintenir mes stocks à jour</strong> sur la plateforme ChimayBois.
                </label>
              </div>
              <div className="flex items-start gap-3">
                <Checkbox
                  id="phone-commitment"
                  checked={phoneCommitment}
                  onCheckedChange={(checked) => setPhoneCommitment(checked === true)}
                  className="mt-0.5"
                />
                <label htmlFor="phone-commitment" className="text-sm text-foreground leading-relaxed cursor-pointer">
                  En mettant en vente, j'accepte que mon <strong>numéro de téléphone soit transmis à l'acheteur</strong> uniquement après le paiement de l'acompte de 10 €.
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          <Button variant="outline" onClick={() => setStep(step - 1)} disabled={step === 0} className="gap-2">
            <ArrowLeft className="h-4 w-4" /> Précédent
          </Button>
          {step < steps.length - 1 ? (
            <Button onClick={() => setStep(step + 1)} className="gap-2">
              Suivant <ArrowRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button className="gap-2 bg-primary" onClick={handlePublish} disabled={submitting || !sellerCommitment || !phoneCommitment}>
              {submitting ? "Publication..." : "Publier l'annonce"} <Check className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CreateListing;
